/**
 * Story 052 — small toast context for the Settings surface.
 *
 * Wraps MUI Snackbar. Sections fire `toast("Saved")` after a successful
 * mutation; the spec asserts on the visible text. Three variants ship:
 * "info" (neutral), "success" (green), "error" (red).
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type PropsWithChildren,
  type ReactElement,
} from "react";
import { Alert, Snackbar } from "@mui/material";

type ToastSeverity = "info" | "success" | "error";

interface ToastApi {
  toast: (message: string, severity?: ToastSeverity) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

interface ToastState {
  open: boolean;
  message: string;
  severity: ToastSeverity;
}

export function ToastProvider({ children }: PropsWithChildren): ReactElement {
  const [state, setState] = useState<ToastState>({
    open: false,
    message: "",
    severity: "info",
  });

  const toast = useCallback(
    (message: string, severity: ToastSeverity = "success") => {
      setState({ open: true, message, severity });
    },
    [],
  );

  const api = useMemo<ToastApi>(() => ({ toast }), [toast]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <Snackbar
        open={state.open}
        autoHideDuration={4000}
        onClose={() => {
          setState((prev) => ({ ...prev, open: false }));
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={state.severity}
          variant="filled"
          sx={{ width: "100%" }}
          onClose={() => {
            setState((prev) => ({ ...prev, open: false }));
          }}
        >
          {state.message}
        </Alert>
      </Snackbar>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast must be used inside <ToastProvider>");
  }
  return ctx;
}

/**
 * DI-3-S6-FE — the toast where a provider is mounted, null where none is (a
 * component test that renders one card without the page's provider).
 */
export function useToastOptional(): ToastApi | null {
  return useContext(ToastContext);
}
